import React from "react";
import { Navigate, Outlet } from "react-router-dom";
import usePermission from "../hooks/usePermission";
import useDefaultRedirect from "../../account/hooks/useDefaultRedirect";
import permissionContext from "../../core/context/permissionContext";
import { PermissionLevel } from "../../core/models/PermissionModels";
import { isMidasPilotUser } from "../../customer/midas/pilotThreadScope";

interface PermissionRouteGuardProps {
  /** The module to check permissions for (use Modules constants). */
  module: string;
  /** The minimum permission level required. Defaults to Read (1). */
  level?: PermissionLevel;
  /**
   * TAJMAC-PILOT-FE3 (owner directive 2026-08-19): admit a MidasPilot regardless of the
   * module level. Set ONLY on the Settings route today.
   *
   * Why it is needed: the pilot is seeded `Settings: 0`, so this guard would bounce them
   * off `/settings` entirely — but the owner requires the Settings surface to be
   * reachable, with every section VISIBLE-but-grayed and only Profile usable.
   *
   * Why it is safe: this opens a UI ROUTE, not data. The pilot's zeroed module
   * permissions are unchanged, so every grayed section's API still 403s — which is
   * exactly what makes the graying honest rather than cosmetic. Confinement inside the
   * surface comes from `useSettingsV2Nav`'s `disabled` flag plus `PilotRouteGuard`, whose
   * allowlist admits only `/settings` + `/settings/profile`. Profile itself is
   * self-service (own password / personal details) and is not Settings-module gated.
   */
  allowMidasPilot?: boolean;
}

/**
 * Route-level permission guard. Renders Outlet if user has sufficient permission,
 * otherwise redirects to default route. Works alongside TypeGuard and AuthorizedRoute.
 *
 * During the transition period, if the JWT does not contain a permissions claim
 * (old token without permissions), access is allowed by default.
 *
 * Usage in routes:
 * ```tsx
 * <Route element={<PermissionRouteGuard module={Modules.Machinery} />}>
 *   <Route path="machinery" element={<Machinery />} />
 * </Route>
 * ```
 */
const PermissionRouteGuard = ({
  module,
  level = PermissionLevel.Read,
  allowMidasPilot = false,
}: PermissionRouteGuardProps) => {
  const { level: userLevel } = usePermission(module);
  const { defaultRoute } = useDefaultRedirect();

  // Transition mode: if the JWT has no permissions claim at all,
  // skip permission check and allow access (backward compatible).
  if (!permissionContext.hasPermissionsClaim()) {
    return <Outlet />;
  }

  // Opt-in per route, defaults false — every existing usage is unchanged.
  if (allowMidasPilot && isMidasPilotUser()) {
    return <Outlet />;
  }

  if (userLevel >= level) {
    return <Outlet />;
  }

  return <Navigate to={defaultRoute()} replace />;
};

export default PermissionRouteGuard;
